import React, { useState } from 'react';
import * as PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash/get';
import Router from 'next/router';

import { confirmWrapper } from '@components/shared/AsyncConfirm';
import API from '@utils/API';
import SuccessModal from '@components/shared/SuccessModal';
import Helpers from '@utils/Helpers';
import Title from '@components/shared/Title';
import Link from '@components/shared/Link';
import Chips from '@components/shared/Chips';
import Avatar from '@components/shared/Avatar';
import ActionIcon from '@components/shared/ActionIcon';
import Breadcrumbs from '@components/shared/Breadcrumbs';
import CommentsList from '@components/shared/CommentsList';
import { useCureLikes } from '@hooks/useCureLikes';
import routes from '@constants/routes';
import { apiUrls } from '@constants/api';

const CombinationDetails = ({
  user,
  combination: {
    id,
    created_by,
    supplement_mix,
    products,
    medicine,
    diets,
    life_tips,
    ...story
  },
  conditionsList,
  route: { query }
}) => {
  const currentUserId = get(user, 'id');
  const isMyOwnCure = get(created_by, 'id') === currentUserId;

  const [deletedPopup, setDeletedPopup] = useState(false);
  const condition = conditionsList.find(item => item.slug === query.condition);

  const breadcrumbs = [
    {
      label: condition.name,
      href: routes.conditions.details.pattern,
      as: routes.conditions.details.path(condition.slug)
    },
    {
      label: 'Success story',
      href: null
    }
  ];

  const { isLiked, likesCount, handleToggleLike } = useCureLikes(
    story,
    apiUrls.combinations.like(id),
    apiUrls.combinations.removeLike(id)
  );

  const handleRemove = async () => {
    const answer = await confirmWrapper(
      'Are you sure you want to delete this story?',
      {
        acceptText: 'Yes, delete',
        declineText: 'Cancel',
        inverted: true
      }
    );

    if (!answer) {
      return;
    }

    await API.delete(apiUrls.combinations.details(id));
    setDeletedPopup(true);
  };

  return (
    <div className="combination combination-details">
      <div className="container">
        <Breadcrumbs
          list={breadcrumbs}
          className="combination-details__breadcrumbs"
        />

        <div className="combination-details__header">
          <Title bold={true} size="lg" className="combination-details__title">
            Success story
          </Title>
        </div>

        <div className="combination-details__intro">
          <div className="combination-details__profile">
            <Avatar
              src={created_by.profile_avatar}
              alt={created_by.username}
              className="combination-details__avatar"
            />
            <div className="combination-details__author">
              <Title
                heading="h2"
                bold={true}
                size="xs"
                className="combination-details__name"
              >
                {created_by.username}
              </Title>
              <Link
                {...Helpers.getUserLink(created_by.id)}
                icon="chevron-right"
                className="combination-details__link"
              >
                See profile
              </Link>
            </div>
          </div>

          <div className="combination-details__actions">
            <div className="combination-details__action">
              <ActionIcon
                type="like"
                isActive={isLiked}
                onClick={handleToggleLike}
                className="combination-details__like"
              />
              <div className="combination-details__count">
                {Helpers.formatLikesAmount(likesCount)}
              </div>
            </div>
          </div>
        </div>

        <div className="combination-details__body">
          <div className="row">
            <div className="col-xxs-12 col-md-9 col-lg-9">
              {life_tips && (
                <div className="combination-details__text">{life_tips}</div>
              )}

              {Helpers.arrayHasItems(supplement_mix) && (
                <Chips
                  title="Supplement Mix"
                  className="combination-details__chips"
                  array={supplement_mix}
                  getChipLabel={chip => chip.name}
                />
              )}

              {Helpers.arrayHasItems(products) && (
                <Chips
                  title="Product Suggestions"
                  className="combination-details__chips"
                  array={products}
                  getChipLabel={chip => chip.name}
                />
              )}

              {Helpers.arrayHasItems(medicine) && (
                <Chips
                  title="Medicine"
                  className="combination-details__chips"
                  array={medicine}
                  getChipLabel={text => text}
                />
              )}

              {Helpers.arrayHasItems(diets) && (
                <Chips
                  title="Diet"
                  className="combination-details__chips"
                  array={diets}
                  getChipLabel={chip => chip.name}
                />
              )}
            </div>
            <div className="col-xxs-12 col-md-3 col-lg-3">
              {isMyOwnCure && (
                <div className="combination-details__controls">
                  <Link
                    className="combination-details__controls-edit"
                    href={routes.combinations.edit.pattern}
                    as={routes.combinations.edit.path(condition.slug, id)}
                  >
                    Edit
                  </Link>
                  <Link
                    className="combination-details__controls-delete"
                    bold
                    onClick={handleRemove}
                  >
                    Delete
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="combination-details__comments">
          <CommentsList
            getUrl={apiUrls.combinations.comments.list(id)}
            createUrl={apiUrls.combinations.comments.create(id)}
          />
        </div>
      </div>
      <SuccessModal
        title="Your success story was deleted"
        isOpen={deletedPopup}
        onClose={() =>
          Router.push(routes.conditions.details.path(condition.slug))
        }
      />
    </div>
  );
};

CombinationDetails.propTypes = {
  user: PropTypes.any,
  route: PropTypes.object,
  combination: PropTypes.object,
  conditionsList: PropTypes.array
};

const mapStateToProps = ({
  auth: { user },
  conditions: { conditionsList }
}) => ({
  user,
  conditionsList
});

export default connect(mapStateToProps)(CombinationDetails);
